/*
 * bc-cart-free-shipping.js — keeps the cart free-shipping progress bar
 * (sections/bc-cart-free-shipping.liquid) in sync as the cart changes.
 *
 * The bar's initial state is rendered server-side. This custom element listens
 * for the theme's global 'cart:update' event (assets/events.js → ThemeEvents.
 * cartUpdate = 'cart:update'), which carries the fresh cart object on
 * event.detail.resource. We read total_price/item_count from there; if a given
 * event doesn't include the full cart, we fall back to a /cart.js fetch.
 *
 * Plain (non-module) script loaded with `defer`, matching the other bc-*.js.
 */
(function () {
  // Both events carry the fresh cart on event.detail.resource:
  //  - 'cart:update'     → qty change, item add/remove (CartUpdateEvent/CartAddEvent)
  //  - 'discount:update' → discount code applied/removed via the cart-page
  //                        discount field (assets/cart-discount.js → DiscountUpdateEvent)
  // We must handle both, else the bar goes stale when a code is entered on /cart.
  var CART_EVENTS = ['cart:update', 'discount:update'];

  class CartFreeShippingBar extends HTMLElement {
    connectedCallback() {
      this.fillEl = this.querySelector('[data-ref="fill"]');
      this.messageEl = this.querySelector('[data-ref="message"]');
      this.threshold = parseInt(this.dataset.threshold, 10) || 0; // cents
      this.currency = this.dataset.currency || 'USD';
      this.msgProgress = this.dataset.msgProgress || '';
      this.msgSuccess = this.dataset.msgSuccess || '';
      this.onCartUpdate = this.onCartUpdate.bind(this);
      CART_EVENTS.forEach((name) => document.addEventListener(name, this.onCartUpdate));
    }

    disconnectedCallback() {
      CART_EVENTS.forEach((name) => document.removeEventListener(name, this.onCartUpdate));
    }

    async onCartUpdate(event) {
      var resource = event && event.detail ? event.detail.resource : null;
      var total = resource && typeof resource.total_price === 'number' ? resource.total_price : null;
      var count = resource && typeof resource.item_count === 'number' ? resource.item_count : null;

      // Fall back to the authoritative cart if the event didn't carry totals.
      if (total === null || count === null) {
        try {
          var res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
          var cart = await res.json();
          total = cart.total_price;
          count = cart.item_count;
        } catch (e) {
          return; // leave the server-rendered state in place
        }
      }

      this.render(total, count);
    }

    render(total, count) {
      if (!count) {
        this.hidden = true;
        return;
      }
      this.hidden = false;

      var pct = this.threshold > 0 ? Math.min(100, Math.round((total / this.threshold) * 100)) : 0;
      if (this.fillEl) this.fillEl.style.width = pct + '%';

      var remaining = this.threshold - total;
      if (remaining <= 0) {
        this.classList.add('is-complete');
        if (this.messageEl) this.messageEl.textContent = this.msgSuccess;
      } else {
        this.classList.remove('is-complete');
        if (this.messageEl) {
          // Wrap the amount in a styled span (colored/larger, see the section
          // stylesheet). Escape the merchant message + amount before injecting.
          var amountHtml =
            '<span class="bc-cart-free-shipping__amount">' + this.escapeHtml(this.formatMoney(remaining)) + '</span>';
          this.messageEl.innerHTML = this.escapeHtml(this.msgProgress).replace('[amount]', amountHtml);
        }
      }
    }

    escapeHtml(str) {
      return String(str).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    formatMoney(cents) {
      return window.bcMoney.formatCents(cents, this.currency);
    }
  }

  if (!customElements.get('cart-free-shipping-bar')) {
    customElements.define('cart-free-shipping-bar', CartFreeShippingBar);
  }
})();
